import React, { FC, RefObject, useEffect } from 'react'
import { REQUEST_CONFIGS_KEYS } from '@services/constants'
import { RequestInputFiles } from '@utils/request'
import * as utilsList from './__index.utils'
import BlobUploader from '../inputs/azureBlobUploader.input'
import { documentStatusConfig } from './__documents.config'

const Step7Documents = ({
	pageAssets,
	pageUtils,
	formInputs,
	errors,
	isSummaryStep = false,
	// *******

	formFilesInputs,
	updateFileInput,
	updateUploadedFilesMap,
	onSetErrorFromFileComponent,

	// ********
	updateFileRequiredByDocumentsConfig,
	disabledBlobUploader,
	fieldWarnings,
	totalFilesCount,
	toPrint = false
}) => {
	const {
		whatToDo,
		isSubjectToPiiaApproval,
		hasBeenSubjectToApproval
		// **********************
	} = formInputs

	const {
		planImplantationArpenteurGrandeEmpriseInput,
		planImplantationFaitMainInput,
		planArchitecturePreliminaireInput,
		planAmenagementInput,
		planAmenagementPaysagerPreliminaireInput,
		ficheBatimentInput,
		planArchitectureInput,
		certificatDeLocalisationInput,
		planStructureInput,
		etudeAcoustiqueInput,
		engagementExemptDeContaminantInput,
		planIngenieurInput,
		planCivilInput,
		planMecaniqueInput,
		planElectriqueInput,
		planVentilationInput,
		photographieInput,
		planAEchelleInput,
		bailSiRequisInput,
		autresSiRequisInput
	} = formFilesInputs

	// check if isIA
	// to use value from summary answers or from inputForm.value
	enum PIIACateg {
		Q3TrueQ4True = 'Q3TrueQ4True',
		Q3TrueQ4False = 'Q3TrueQ4False',
		Q3False = 'Q3False'
	}

	const isPIIACategPermitDocument = () => {
		return isSubjectToPiiaApproval.value.toString() === 'true' &&
			hasBeenSubjectToApproval.value.toString() === 'true'
			? PIIACateg.Q3TrueQ4True
			: isSubjectToPiiaApproval.value.toString() === 'true' &&
			  hasBeenSubjectToApproval.value.toString() === 'false'
			? PIIACateg.Q3TrueQ4False
			: PIIACateg.Q3False
	}

	const resolveDocumentCasStatus = () => {
		switch (whatToDo.value.toString()) {
			// ********************************************
			//! GR7_1RenovationOrDevelopment
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_1RenovationOrDevelopment: {
				if (isPIIACategPermitDocument() == PIIACateg.Q3TrueQ4False) {
					return documentStatusConfig.GR7_1RenovationOrDevelopment['IA-68']
				}

				if (
					isPIIACategPermitDocument() == PIIACateg.Q3TrueQ4True ||
					isPIIACategPermitDocument() == PIIACateg.Q3False
				) {
					return documentStatusConfig.GR7_1RenovationOrDevelopment['PA-14']
				}
			}
			// ********************************************
			//! GR7_2RequestOccupation
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_2RequestOccupation: {
				// un seul cas pour le moment
				return documentStatusConfig.GR7_2RequestOccupation['CO-7']
			}

			// ********************************************
			//! GR7_3HomeOfficeOrBusiness
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_3HomeOfficeOrBusiness: {
				return documentStatusConfig.GR7_3HomeOfficeOrBusiness['CO-9']
			}

			// ********************************************
			//! GR7_4InstallationOfEnseigne
			case REQUEST_CONFIGS_KEYS.whatToDoCommercialPermit
				.GR7_4InstallationOfEnseigne: {
				if (isPIIACategPermitDocument() == PIIACateg.Q3TrueQ4False) {
					return documentStatusConfig.GR7_4InstallationOfEnseigne['IA-60']
				}

				if (
					isPIIACategPermitDocument() == PIIACateg.Q3TrueQ4True ||
					isPIIACategPermitDocument() == PIIACateg.Q3False
				) {
					return documentStatusConfig.GR7_4InstallationOfEnseigne['CA-8']
				}
			}
			// ********************************************

			default:
				return {}
		}

		return {}
	}

	useEffect(() => {
		// update files required by the document status config
		const config = resolveDocumentCasStatus()

		updateFileRequiredByDocumentsConfig(config)
	}, [])

	const getDocumentStatus = (document: string, config): string => {
		const categoryConfig = config

		if (categoryConfig) {
			return categoryConfig[document] || 'not_required'
		}

		return 'not_required'
	}

	const isDocumentToShow = (document: string, config, status: string) => {
		return getDocumentStatus(document, config) == status
	}

	// a function to render RequestFiles on the DOCUMENT or Summary step
	const renderRequestFile = (
		input: RequestInputFiles,
		config,
		status: string
	) => {
		if (!isSummaryStep || (isSummaryStep && input.files.size > 0)) {
			return (
				isDocumentToShow(input.name, config, status) && (
					<BlobUploader
						name={input.name}
						subtitle={input.label}
						required={input.required}
						description={input.description}
						files={input.files}
						uploadedFilesMap={input.uploadedFilesMap}
						hasError={errors.includes(input.name)}
						onSetFiles={(files) => updateFileInput(input.name, files)}
						setUploadedFilesMap={(uploadedFilesMap) =>
							updateUploadedFilesMap(input.name, uploadedFilesMap)
						}
						maxFiles={input.numberMaxFiles}
						onSetHasError={onSetErrorFromFileComponent}
						isSummary={isSummaryStep}
						disabled={disabledBlobUploader}
						fieldWarnings={fieldWarnings}
						totalFilesCount={totalFilesCount}
						toPrint={toPrint}
					/>
				)
			)
		}
	}

	// show the files on the DOCUMENT step
	// @status : the status of the document (required or optionnal)
	// @isSummary : if the step is the summary step
	const renderRequestFiles = (status: string) => {
		const config = resolveDocumentCasStatus()
		return (
			<>
				{renderRequestFile(
					planImplantationArpenteurGrandeEmpriseInput,
					config,
					status
				)}
				{renderRequestFile(planImplantationFaitMainInput, config, status)}
				{renderRequestFile(certificatDeLocalisationInput, config, status)}
				{renderRequestFile(planAmenagementInput, config, status)}
				{renderRequestFile(
					planAmenagementPaysagerPreliminaireInput,
					config,
					status
				)}
				{renderRequestFile(planArchitecturePreliminaireInput, config, status)}
				{renderRequestFile(planArchitectureInput, config, status)}

				{renderRequestFile(planMecaniqueInput, config, status)}
				{renderRequestFile(planElectriqueInput, config, status)}
				{renderRequestFile(planVentilationInput, config, status)}

				{renderRequestFile(planCivilInput, config, status)}
				{renderRequestFile(etudeAcoustiqueInput, config, status)}
				{renderRequestFile(engagementExemptDeContaminantInput, config, status)}
				{renderRequestFile(planIngenieurInput, config, status)}
				{renderRequestFile(planStructureInput, config, status)}
				{renderRequestFile(ficheBatimentInput, config, status)}

				{renderRequestFile(photographieInput, config, status)}
				{renderRequestFile(planAEchelleInput, config, status)}
				{renderRequestFile(bailSiRequisInput, config, status)}

				{renderRequestFile(autresSiRequisInput, config, status)}
			</>
		)
	}

	return (
		<>
			{!isSummaryStep && (
				<>
					<h4 className={pageUtils.classes.h4}>
						{pageAssets?.request_form_required_documents}
					</h4>
					<span className={pageUtils.classes.fileInfo}>
						{pageAssets?.request_form_required_documents_description}
					</span>
				</>
			)}

			{renderRequestFiles('required')}
			{renderRequestFiles('optional')}
		</>
	)
}

export default Step7Documents
